import { useCallback, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { forEach } from 'lodash'

import { useLenis } from '@/contexts/lenis'
import { useTheme } from '@/contexts/theme'
import { useMotionTouchMedia } from '@/hooks'
import { useLayoutStore } from '@/store/layout'
import { useLoaderStore } from '@/store/loader'

import {
  createImageMaskMotion,
  createLinesMotion,
  createMaskMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useContactFormMotion = () => {
  const { theme } = useTheme()
  const { lenis } = useLenis()
  const motionRef = useRef<HTMLDivElement>(null)
  const scrollTriggers = useRef<ScrollTrigger[]>([])
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const setLayoutState = useLayoutStore((state) => state.setState)

  const info1Motion = useRef<Motion | null>(null)
  const info2Motion = useRef<Motion | null>(null)
  const titleMotion = useRef<Motion | null>(null)
  const paragraphMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    info1Motion.current?.revert()
    info2Motion.current?.revert()
    titleMotion.current?.revert()
    paragraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const cols = selector?.('[data-motion-col]')
        const info1 = selector?.('[data-motion-info-1]')
        const info2 = selector?.('[data-motion-info-2]')
        const image = selector?.('[data-motion-image]')
        const imageMask = selector?.('[data-motion-image-mask]')
        const title = selector?.('[data-motion-title]')
        const paragraph = selector?.('[data-motion-paragraph]')
        const fields = selector?.('form>div')

        info1Motion.current = info1 ? createLinesMotion(info1) : null
        info2Motion.current = info2 ? createLinesMotion(info2) : null
        titleMotion.current = title ? createMaskMotion(title) : null
        paragraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        // animation 1
        const animation1 = gsap.timeline({
          /* onComplete: revertSplitText */
        })
        info1Motion.current && animation1.add(info1Motion.current.timeline, 0)
        info2Motion.current && animation1.add(info2Motion.current.timeline, 0)
        image && imageMask && animation1.add(createImageMaskMotion(image, imageMask), 0)

        // animation 2
        const animation2 = gsap.timeline()
        titleMotion.current && animation2.add(titleMotion.current.timeline, 0)
        paragraphMotion.current && animation2.add(paragraphMotion.current.timeline, 0.2)
        fields.length > 0 &&
          animation2.from(
            fields,
            {
              y: 30,
              opacity: 0,
              duration: 0.5,
              ease: 'power2.out',
              stagger: {
                amount: 0.2
              }
            },
            0.2
          )

        scrollTriggers.current = []
        forEach(cols, (col, index: number) => {
          const animation = index === 0 ? animation1 : animation2
          const scrollTrigger = ScrollTrigger.create({
            ...getScrollTriggerOptions({
              trigger: col,
              animation
            })
          })
          scrollTriggers.current.push(scrollTrigger)
        })
      },

      revert() {
        forEach(scrollTriggers.current, (st) => st.kill())
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  /**
   * theme update setup
   */
  useGSAP(
    () => {
      if (!lenis) return

      const scrollTrigger = ScrollTrigger.create({
        trigger: motionRef.current,
        start: 'top bottom',
        end: () => ScrollTrigger.maxScroll(window),
        onEnter: () => setLayoutState({ footerTheme: 'dark' }),
        onLeaveBack: () => setLayoutState({ footerTheme: theme.footer }),
        markers: false
      })

      return () => {
        scrollTrigger.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  return { motionRef }
}
